import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQuery } from 'react-query';
import { LocationsApi } from '@frontend/api-locations';
import PhoneHoursApi from '@frontend/api-phone-hours';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { usePhoneLocationFilterShallowStore, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { usePhoneOverrideShallowStoreV2 } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, Text, TextLink, Truncated, useModalControl, useTooltip } from '@frontend/design-system';
import { IndexTable } from '../../components/common/index-table';
import { PhoneHoursTray } from '../../components/phone-hours/phone-hours-edit-tray';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';
import { useHoursManagementPermission } from '../../userManagementPermission';

export function PhoneHours() {
  const { t } = useTranslation('phone');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { navigate } = useSettingsNavigate();
  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const { selectedLocationIds } = useAppScopeStore();
  const filteredLocations = locationFilters.phoneHoursIndex;
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const isUnify = settingsTenantLocation?.locationType === 'unify';
  const phoneTenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const sideModal = useModalControl();
  const [searchValue, setSearchValue] = useState('');
  const hasPhoneFullAccess = hasSchemaACL(settingsTenantLocation?.id ?? '', Permission.PHONE_FULL_ACCESS);

  const { data: phoneHoursData = [], isLoading } = useQuery({
    queryKey: [phoneTenantId, ...queryKeys.settings.listPhoneHours()],
    queryFn: async () => PhoneHoursApi.ListPhoneHours({ tenantId: phoneTenantId }).then((res) => res.phoneHours),
    select: (data) =>
      (filteredLocations.length
        ? data?.filter((phoneHours) => {
            return filteredLocations.includes(phoneHours.locationId);
          })
        : data
      )?.sort((a, b) => a.name.localeCompare(b.name)),
    enabled: !!phoneTenantId,
    meta: {
      error: {
        message: 'PhoneHours:phoneHoursQuery | Failed to fetch phone hours',
        data: { phoneTenantId, isUnify, hasPhoneFullAccess },
      },
    },
  });
  const phoneHoursListData = phoneHoursData.filter(
    (item) => item.scheduleCallRoute?.length && selectedLocationIds.includes(item.locationId)
  );

  const filteredTableData = useMemo(() => {
    const value = searchValue.trim().toLowerCase();
    if (!value) {
      return phoneHoursListData;
    }

    return phoneHoursListData.filter((row) => {
      const nameMatch = row.name.toLowerCase().includes(value);
      const routeMatch = row.scheduleCallRoute?.some((route) => route.name.toLowerCase().includes(value));
      return nameMatch || routeMatch;
    });
  }, [phoneHoursListData, searchValue]);

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [calendarViewState, setCalendarViewState] = useState<boolean>(false);

  const { data: selectedPhoneHour } = useQuery({
    queryKey: [phoneTenantId, ...queryKeys.settings.readPhoneHour(selectedId ?? '')],
    queryFn: () => PhoneHoursApi.ReadPhoneHours({ id: selectedId ?? '', tenantId: phoneTenantId }),
    enabled: !!selectedId,
    keepPreviousData: true,
  });

  const currentIndex = phoneHoursListData.findIndex((item) => item.id === selectedId);
  const isPreviousDisabled = currentIndex <= 0;
  const isNextDisabled = currentIndex >= phoneHoursListData.length - 1;
  const currentPhoneHour = phoneHoursListData[currentIndex];

  const handlePreviousClick = () => {
    if (!isPreviousDisabled) {
      setSelectedId(phoneHoursListData[currentIndex - 1].id);
    }
  };

  const handleNextClick = () => {
    if (!isNextDisabled) {
      setSelectedId(phoneHoursListData[currentIndex + 1].id);
    }
  };

  const selectedLocationId = phoneHoursListData.find((item) => item.id === selectedPhoneHour?.id)?.locationId ?? 'null';

  const { data: officeTimezone } = useQuery({
    queryKey: [selectedLocationId, ...queryKeys.timezone(selectedLocationId || '')],
    queryFn: () => LocationsApi.getOfficeTimezone({}, { locationId: selectedLocationId }),
    select: (data) => data.timezone,
    enabled: !!selectedLocationId && selectedLocationId !== 'null',
  });

  // Handle calendar view state changes
  const handleCalendarViewChange = (isOpen: boolean) => {
    setCalendarViewState(isOpen);
  };

  const handleEditClickFromTable = (phoneHourId: string) => {
    setSelectedId(phoneHourId);
    setIsEditMode(true);
    sideModal.openModal();
  };

  useEffect(() => {
    // Clear selectedId when modal is closed
    if (!sideModal.modalProps.show) {
      setSelectedId(null);
      setIsEditMode(false);
    }
  }, [sideModal.modalProps.show]);

  const ActionIcon = ({
    iconName,
    tooltipText,
    onClick,
    hasPermission = true,
    disabled = false,
  }: {
    iconName: 'edit' | 'view';
    tooltipText: string;
    onClick: () => void;
    hasPermission?: boolean;
    disabled?: boolean;
  }) => {
    const { tooltipProps, triggerProps, Tooltip } = useTooltip();

    return (
      <div {...triggerProps}>
        <Button
          onClick={onClick}
          iconName={iconName}
          variant='secondary'
          size='large'
          disabled={disabled}
          css={css`
            width: 40px;
            svg {
              color: ${disabled ? theme.colors.neutral20 : theme.colors.neutral50};
            }
          `}
        />
        <Tooltip {...tooltipProps} css={{ width: hasPermission ? undefined : theme.spacing(29) }}>
          {tooltipText}
        </Tooltip>
      </div>
    );
  };

  const isOverrideEnabled = (usePhoneOverrideShallowStoreV2('overridesV2')?.overridesV2?.phoneOverrides ?? []).some(
    (override) => override.callRouteId === selectedPhoneHour?.callRouteIds?.[0] && override.enabled
  );

  const hoursManagementPermission = useHoursManagementPermission();

  return (
    <>
      <PhoneSettingsPage
        title={t('Phone Hours')}
        subtitle={
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(2)};
            `}
          >
            {t('Define hours in order to route calls differently by time of day.')}
          </div>
        }
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Phone Hours'), to: '/phone/phone-hours' },
        ]}
      >
        {settingsTenantLocation ? (
          <IndexTable
            labelsColumnPosition={2}
            tenantLocation={settingsTenantLocation}
            idAccessor={(data) => data.id}
            nameAccessor={(data) => data.name}
            labelsAccessor={(data) => [data.locationId]}
            filteredLocations={filteredLocations}
            setFilteredLocations={(locations) => {
              setLocationFilters({ phoneHoursIndex: locations });
            }}
            showDelete={false}
            tableConfig={{
              colConfig: [
                {
                  id: 'name',
                  Header: t('Phone Hours Name'),
                  accessor: (row) => row, // return the whole row object
                  sortType: (rowA, rowB, columnId) => {
                    const a = rowA.values?.[columnId]?.name || '';
                    const b = rowB.values?.[columnId]?.name || '';
                    return a.localeCompare(b);
                  },
                  cellRenderer: (row) => (
                    <Truncated
                      as={TextLink}
                      textAlign='left'
                      onClick={() => {
                        setSelectedId(row.id);
                        sideModal.openModal();
                      }}
                    >
                      {row.name}
                    </Truncated>
                  ),
                },
                {
                  id: 'callRoute',
                  Header: t('Call Route'),
                  accessor: (data) => data.scheduleCallRoute?.[0],
                  cellRenderer: (value) =>
                    hasPhoneFullAccess ? (
                      <Truncated
                        as={TextLink}
                        textAlign='left'
                        onClick={() =>
                          navigate({
                            to: '/phone/call-routes/:id',
                            params: { id: value.id ?? '' },
                          })
                        }
                      >
                        {value.name}
                      </Truncated>
                    ) : (
                      <Truncated as={Text} size='medium' color='default'>
                        {value.name}
                      </Truncated>
                    ),
                  sortType: (rowA, rowB, columnId) => {
                    const a = rowA.values?.[columnId]?.name || '';
                    const b = rowB.values?.[columnId]?.name || '';
                    return a.localeCompare(b);
                  },
                },
                {
                  id: 'viewAction',
                  Header: '',
                  disableSortBy: true,
                  maxWidth: 10,
                  accessor: (data) => data,
                  cellRenderer: (row) => (
                    <ActionIcon
                      iconName='view'
                      tooltipText={t('View phone hours')}
                      onClick={() => {
                        setSelectedId(row.id);
                        sideModal.openModal();
                      }}
                    />
                  ),
                },
                {
                  id: 'editAction',
                  Header: '',
                  disableSortBy: true,
                  maxWidth: 10,
                  accessor: (data) => data,
                  cellRenderer: (row) => {
                    const hasPermission = hoursManagementPermission(row.locationId);
                    return (
                      <ActionIcon
                        disabled={!hasPermission}
                        iconName='edit'
                        hasPermission={hasPermission}
                        tooltipText={
                          hasPermission
                            ? t('Edit phone hours')
                            : t('You do not have the necessary permissions to take this action.')
                        }
                        onClick={() => handleEditClickFromTable(row.id)}
                      />
                    );
                  },
                },
              ],
              data: filteredTableData ?? [],
              isLoading,
              hasGlobalSearch: true,
              globalSearchConfig: {
                searchHandler: (inputValue) => setSearchValue(inputValue?.trim().length < 3 ? '' : inputValue?.trim()),
                debounceDelay: 500,
              },
            }}
          />
        ) : (
          <div>{t('No Phone Tenant Selected')}</div>
        )}
      </PhoneSettingsPage>
      {selectedPhoneHour?.id === selectedId && (
        <div id='phone-hours-section'>
          {sideModal.modalProps.show && (
            <PhoneHoursTray
              key={selectedPhoneHour.id}
              modalControls={sideModal}
              selectedPhoneHour={selectedPhoneHour}
              mountTarget='#phone-hours-section'
              selectedLocationId={selectedLocationId}
              currentPhoneHour={currentPhoneHour}
              onPreviousClick={handlePreviousClick}
              onNextClick={handleNextClick}
              isPreviousDisabled={isPreviousDisabled}
              isNextDisabled={isNextDisabled}
              isOverrideEnabled={isOverrideEnabled}
              scheduleCallRoutes={
                phoneHoursListData.find((item) => item.id === selectedPhoneHour.id)?.scheduleCallRoute ?? []
              }
              initialCalendarViewState={calendarViewState}
              onCalendarViewChange={handleCalendarViewChange}
              timezone={officeTimezone}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
            />
          )}
        </div>
      )}
    </>
  );
}
